import {Cancel} from 'assets/svgs'
import classNames from 'classnames'
import {ExemptRegions} from 'common'
import {ExemptionType} from 'common/enums'
import Button from 'components/Button'
import DropDownSelect from 'components/DropDown'
import {useFirebase} from 'components/Firebase/hooks'
import ModalComponent from 'components/ModalComponent'
import {Loader} from 'components/Loader'
import {
  filteredCountries as COUNTRIES,
  getCountryIsoCode,
  getCountryAndStateNamesByCode,
  getStatesOfCountryWithTwoLetterIso,
  memoizeFunc
} from 'components/utils'
import {useFormik} from 'formik'
import {filter, negate, replace, startCase} from 'lodash'
import {useEffect, useState} from 'react'
import {toast} from 'react-toastify'
import styles from './ExemptionRegionsAndTypeModal.module.scss'

const ExemptionRegionsAndTypeModal = ({
  show,
  contactId,
  accountId,
  handleClose,
  exemptRegions,
  exemptionType
}: {
  exemptionType?: ExemptionType
  exemptRegions?: Array<ExemptRegions>
  handleClose: () => void
  contactId: string
  accountId: string
  show: boolean
}) => {
  const [disabled, setDisabled] = useState(false)
  const [regionsList, setRegionsList] = useState<any>(null)
  const {updateExemptionTypeAndRegions} = useFirebase()
  const [loader, setLoader] = useState(true)
  const handleSubmit = async (values: {
    exemptionType?: ExemptionType
    regions?: Array<ExemptRegions>
    country?: string
    state?: string
  }) => {
    if (disabled) return
    setDisabled(true)
    const {message, success} = await updateExemptionTypeAndRegions({
      userId: contactId,
      accountId,
      exemptionType: values.exemptionType,
      exemptRegions: values.regions
    })
    success ? toast.success(message) : toast.error(message)
    setDisabled(false)
    handleClose()
    return
  }

  const {
    values,
    handleSubmit: onSubmit,
    handleBlur,
    touched,
    setFieldValue
  } = useFormik({
    initialValues: {
      regions: exemptRegions || [],
      exemptionType: exemptionType || ExemptionType.NonExempt,
      country: '',
      state: ''
    },
    onSubmit: handleSubmit
  })

  const [state, setState] = useState<any>([])
  const [filteredCountries, setFilteredCountries] = useState<any>([])
  useEffect(() => {
    (async () => {
      const data: any = await COUNTRIES()
      setFilteredCountries(data)
    })()
  }, [])

  useEffect(() => {
    (async () => {
      setState(await memoizedStatesOfCountry(await getCountryIsoCode(values.country)) || [])
    })()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values])

  const countryValue = filteredCountries?.findIndex(
    (country: any) => country.isoCode === values.country
  )
  const memoizedStatesOfCountry = memoizeFunc(
    getStatesOfCountryWithTwoLetterIso
  )
  
  const exemptionTypeOptions = Object.values(ExemptionType).map((etype) => ({
    name: startCase(replace(etype || ExemptionType.NonExempt, /_/g, ' ')),
    value: etype
  }))
  const exemptionTypeValue = exemptionTypeOptions.findIndex(
    (type) => type.value === values.exemptionType
  )

  const handleAdd = () => {
    const {country, state} = values
    if (!country) {
      toast.warn('Please select a country')
      return
    }
    if (!state) {
      toast.warn('Please select a state')
      return
    }
    const alreadyExists = values.regions.some(
      (region) => region.country === country && region.state === state
    )
    if (alreadyExists) {
      toast.warn('Selected region is already in list')
      return
    }
    setFieldValue('regions', [
      ...values?.regions,
      {country: values.country, state: values.state}
    ])
  }

  const handleRemove = (country?: string, state?: string) => {
    const updatedRegions = filter(
      values?.regions,
      negate((region) => region.country === country && region.state === state)
    )
    setFieldValue('regions', updatedRegions)
  }

  useEffect(() => {
    (async ()=> {
      setLoader(true)
      const elementArr = [];
      let regLen = values?.regions?.length;
      if(regLen > 0){
        for(let i=0; i<regLen; i++){
          const {country, state} = await getCountryAndStateNamesByCode(values?.regions?.[i]?.state || '', values?.regions?.[i]?.country || '');
          elementArr.push(
            <div
              key={`region-${country}-${state}-${i}`}
              className={styles.regions}
            >
              <div>
                <b>Country: </b>
                {country || values?.regions?.[i]?.country}
              </div>
              <div>
                <b>State: </b>
                {state || values?.regions?.[i]?.state}
              </div>
              <button
                type="button"
                onClick={() => handleRemove(values?.regions?.[i]?.country, values?.regions?.[i]?.state)}
                className={classNames('btn-fill-red', styles.removeBtn)}
              >
                Remove
              </button>
            </div>
          );
        }
      }
      setRegionsList(elementArr);
      setLoader(false)
    })();
  }, [values]);

  return (
    <ModalComponent handleClose={handleClose} show={show}>
      <div className={styles.container}>
        <form onSubmit={onSubmit} className={styles.content}>
          <div className={styles.heading}>
            <Cancel className={styles.cancel} onClick={handleClose} />
            <div className={styles.infoContainer}></div>
          </div>
          <h1>Exemption Type</h1>
          <div className={styles.dropdowns}>
            <DropDownSelect
              name="exemptionType"
              placeholder="Exemption Type"
              value={exemptionTypeValue}
              onBlur={handleBlur}
              touched={touched}
              customClass={styles.exemptionTypeDropdown}
              options={exemptionTypeOptions}
              onChange={(index) =>
                setFieldValue(
                  'exemptionType',
                  exemptionTypeOptions[index].value
                )
              }
            />
          </div>
          <h1>Exempt Regions</h1>
          <div className={styles.dropdowns}>
            <DropDownSelect
              name="country"
              mode="country"
              options={filteredCountries}
              placeholder="Country"
              onBlur={handleBlur}
              touched={touched}
              value={countryValue}
              cancelClickFunction={() => {
                setFieldValue('country', '')
                setFieldValue('state', '')
              }}
              onChange={(index) => {
                setFieldValue('country', filteredCountries[index].isoCode)
                setFieldValue('state', '')
              }}
              disabled={disabled}
            />
            <DropDownSelect
              name="state"
              mode="state"
              options={state}
              disabled={disabled}
              onBlur={handleBlur}
              touched={touched}
              placeholder="State"
              value={state?.findIndex(
                (state: any) => state.isoCode === values.state
              )}
              cancelClickFunction={() => {
                setFieldValue('state', '')
              }}
              onChange={(index) => {
                setFieldValue('state', state[index].isoCode)
              }}
            />
            <Button
              text="Add"
              type="button"
              onClick={handleAdd}
              customClass={styles.btnAdd}
            />
          </div>
          {loader ? <Loader fullHeight={false} /> : regionsList}
          <div className={styles.buttons}>
            <button
              type="button"
              className="btn-fill-grey"
              onClick={handleClose}
            >
              Cancel
            </button>
            <Button
              type="submit"
              text="Confirm"
              disabled={disabled}
              customBtnClass="btn-fill-green"
            />
          </div>
        </form>
      </div>
    </ModalComponent>
  )
}

export default ExemptionRegionsAndTypeModal
